/**
 * Fleet Health — a computed metrics dashboard, built to the Parallax design.
 *
 * Everything here is derived from the live fleet on a slow interval (not the
 * 10 Hz hot path): KPIs, readiness by team, a battery histogram, active alerts,
 * and subsystem proxies. It complements the map, never replaces it.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { getFleetHealth, type FleetHealth, type HealthGrade } from '@/api/fleetHealth';
import { InventoryPanel } from './InventoryPanel';
import { MaintenancePanel } from './MaintenancePanel';
import { JocPanel } from './JocPanel';
import { SilvusMeshPanel } from './SilvusMeshPanel';
import { CommanderIntent } from '@/features/command/CommanderIntent';
import { PopoutPanel } from '@/features/command/PopoutPanel';
import { getSeparation, type Conflict } from '@/api/separation';
import { useDroneTeams } from '@/features/fleet/FleetRail';
import { aorById, positionInAor } from '@/map/aors';
import { useAlerts, useDroneStore } from '@/stores/droneStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import type { Drone } from '@/types/domain';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const WHITE = '#f4f4f5';
const MUTED = 'rgba(244,244,245,0.5)';
const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

const REFRESH_MS = 1500;

type Status = 'up' | 'degraded' | 'down';

function linkPercent(drone: Drone): number | null {
  const rc = drone.telemetry['RC_CHANNELS'] as Record<string, unknown> | undefined;
  const rssi = typeof rc?.rssi === 'number' ? rc.rssi : null;
  return rssi === null || rssi === 255 ? null : Math.round((rssi / 254) * 100);
}

function statusOf(drone: Drone): Status {
  if (drone.state === 'stale' || drone.state === 'disconnected') return 'down';
  if ((drone.scalars.gpsFixType ?? 0) < 3) return 'down';
  const link = linkPercent(drone);
  const batt = drone.scalars.batteryPercent;
  if ((link !== null && link < 40) || (batt !== undefined && batt <= 30)) return 'degraded';
  return 'up';
}

interface DerivedAlert {
  cs: string;
  msg: string;
  tone: string;
  edge: string;
}

interface HealthSnapshot {
  total: number;
  up: number;
  degraded: number;
  down: number;
  avgBattery: number | null;
  meanLink: number | null;
  simCount: number;
  batteryBins: number[]; // 5 bins
  teams: Array<{ name: string; color: string; up: number; degraded: number; down: number }>;
  derivedAlerts: DerivedAlert[];
}

function compute(teamMap: Record<string, { callsign: string; color: string }>): HealthSnapshot {
  const aor = aorById(useWorkspaceStore.getState().aor);
  const drones = [...useDroneStore.getState().drones.values()].filter((d) =>
    positionInAor(d.scalars.lat, d.scalars.lon, aor),
  );
  const total = drones.length;
  let up = 0;
  let degraded = 0;
  let down = 0;
  let simCount = 0;
  let battSum = 0;
  let battN = 0;
  let linkSum = 0;
  let linkN = 0;
  const bins = [0, 0, 0, 0, 0];
  const teamAgg = new Map<string, { name: string; color: string; up: number; degraded: number; down: number }>();
  const derivedAlerts: DerivedAlert[] = [];

  for (const drone of drones) {
    const s = statusOf(drone);
    if (s === 'up') up += 1;
    else if (s === 'degraded') degraded += 1;
    else down += 1;
    if (drone.simulated) simCount += 1;

    const batt = drone.scalars.batteryPercent;
    if (batt !== undefined) {
      battSum += batt;
      battN += 1;
      const bi = Math.min(4, Math.floor(batt / 20));
      bins[bi] = (bins[bi] ?? 0) + 1;
    }
    const link = linkPercent(drone);
    if (link !== null) {
      linkSum += link;
      linkN += 1;
    }

    // team rollup
    const team = teamMap[drone.droneId];
    const key = team?.callsign ?? 'UNASSIGNED';
    const agg =
      teamAgg.get(key) ??
      {
        name: key,
        color: team?.color ?? 'rgba(244,244,245,0.4)',
        up: 0,
        degraded: 0,
        down: 0,
      };
    agg[s] += 1;
    teamAgg.set(key, agg);

    // derived alerts (sim excluded from hard alerts)
    if (!drone.simulated) {
      if (link !== null && link < 40)
        derivedAlerts.push({
          cs: drone.callsign,
          msg: `Link degraded — RSSI ${link}%`,
          tone: WARN,
          edge: 'color-mix(in oklch, oklch(0.80 0.15 85) 40%, transparent)',
        });
      else if (batt !== undefined && batt <= 25)
        derivedAlerts.push({
          cs: drone.callsign,
          msg: `Battery ${batt.toFixed(0)}% — below RTL reserve`,
          tone: DANGER,
          edge: 'color-mix(in oklch, oklch(0.62 0.20 28) 45%, transparent)',
        });
      else if ((drone.scalars.gpsFixType ?? 0) < 3)
        derivedAlerts.push({
          cs: drone.callsign,
          msg: 'No GPS fix — holding',
          tone: WARN,
          edge: 'color-mix(in oklch, oklch(0.80 0.15 85) 40%, transparent)',
        });
    }
  }

  return {
    total,
    up,
    degraded,
    down,
    avgBattery: battN ? Math.round(battSum / battN) : null,
    meanLink: linkN ? Math.round(linkSum / linkN) : null,
    simCount,
    batteryBins: bins,
    teams: [...teamAgg.values()].sort((a, b) =>
      a.name === 'UNASSIGNED' ? 1 : b.name === 'UNASSIGNED' ? -1 : a.name.localeCompare(b.name),
    ),
    derivedAlerts,
  };
}

function statusColor(pct: number): string {
  return pct >= 85 ? OK : pct >= 65 ? WARN : DANGER;
}

/** Rolling per-KPI history for the sparklines — real values across the session. */
const HISTORY_CAP = 14;

function sparkPoints(history: number[]): string {
  if (history.length < 2) return '';
  const min = Math.min(...history);
  const max = Math.max(...history);
  const range = max - min || 1;
  return history
    .map((v, i) => {
      const x = (i / (history.length - 1)) * 58 + 1;
      const y = 14 - ((v - min) / range) * 12;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

function KpiCard({
  label,
  value,
  unit,
  color,
  history,
}: {
  label: string;
  value: string;
  unit: string;
  color: string;
  history: number[];
}) {
  return (
    <div
      className="rounded-[18px] border p-[15px]"
      style={{
        background: CARD,
        borderColor: 'rgba(255,255,255,0.13)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
      }}
    >
      <div className="label-mono text-[9px]" style={{ color: 'rgba(244,244,245,0.45)', letterSpacing: '0.14em' }}>
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-[7px]">
        <span className="tnum text-[30px] font-semibold" style={{ color }}>
          {value}
        </span>
        <span className="tnum text-[11px]" style={{ color: MUTED }}>
          {unit}
        </span>
      </div>
      <div className="mt-[9px] h-4">
        <svg width="60" height="16" viewBox="0 0 60 16" preserveAspectRatio="none">
          <polyline points={sparkPoints(history)} fill="none" stroke={color} strokeWidth="1.3" />
        </svg>
      </div>
    </div>
  );
}

function Panel({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div
      className="rounded-[18px] border p-[16px_18px]"
      style={{
        background: CARD,
        borderColor: 'rgba(255,255,255,0.13)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
        padding: '16px 18px',
      }}
    >
      <div className="mb-3.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <span className="label-mono text-[9.5px]" style={{ color: MUTED, letterSpacing: '0.16em' }}>
          {title}
        </span>
        {right}
      </div>
      {children}
    </div>
  );
}

const HISTORY: Record<string, number[]> = {};
function pushHistory(key: string, value: number): number[] {
  const arr = HISTORY[key] ?? [];
  arr.push(value);
  if (arr.length > HISTORY_CAP) arr.shift();
  HISTORY[key] = arr;
  return [...arr];
}

const GRADE_COLOR: Record<HealthGrade, string> = {
  healthy: OK,
  degraded: WARN,
  critical: DANGER,
  unknown: MUTED,
};

const GRADE_LABEL: Record<HealthGrade, string> = {
  healthy: 'HEALTHY',
  degraded: 'DEGRADED',
  critical: 'CRITICAL',
  unknown: 'NO DATA',
};

/**
 * The authoritative fleet grade, from the server composite.
 *
 * Deliberately the loudest thing on the screen and deliberately worst-case: the
 * number is the worst aircraft's, not the average, so it cannot read green
 * while one aircraft cannot get home. The reason sits next to it, because a
 * single number with no "why" is not something a commander can act on.
 */
export function CompositeHealthHero(): ReactNode {
  const [health, setHealth] = useState<FleetHealth | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void getFleetHealth().then((r) => {
        if (!cancelled) setHealth(r.ok ? r.data : null);
      });
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!health) return null;
  const color = GRADE_COLOR[health.grade];

  return (
    <div
      className="rounded-[18px] border p-[16px_20px]"
      style={{
        background: CARD,
        borderColor: `color-mix(in oklch, ${color} 45%, transparent)`,
      }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex flex-col">
          <span
            className="label-mono text-[9px]"
            style={{ color: MUTED, letterSpacing: '0.14em' }}
          >
            FLEET HEALTH
          </span>
          <span className="tnum text-[40px] font-semibold leading-none" style={{ color }}>
            {health.score}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 basis-[200px] flex-col gap-1">
          <span
            className="label-mono w-fit rounded-[99px] px-2 py-0.5 text-[10px]"
            style={{ color, background: `color-mix(in oklch, ${color} 16%, transparent)` }}
          >
            {GRADE_LABEL[health.grade]}
          </span>
          <span className="text-[11px]" style={{ color: WHITE }}>
            {health.worstReason}
          </span>
        </div>
        <div className="ml-auto flex gap-3">
          {(['critical', 'degraded', 'unknown', 'healthy'] as HealthGrade[]).map((grade) => (
            <div key={grade} className="flex flex-col items-center">
              <span className="tnum text-[16px] font-semibold" style={{ color: GRADE_COLOR[grade] }}>
                {health.counts[grade] ?? 0}
              </span>
              <span className="label-mono text-[8px]" style={{ color: MUTED }}>
                {GRADE_LABEL[grade]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HealthView(): ReactNode {
  const teamMap = useDroneTeams();
  const aorId = useWorkspaceStore((s) => s.aor);
  const storeAlerts = useAlerts();
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [snap, setSnap] = useState<HealthSnapshot>(() => compute(teamMap));
  const [, force] = useState(0);
  // Subsystem health comes off the server, which decodes the aircraft's own
  // SYS_STATUS masks. The panel used to invent these from message presence.
  const [fleet, setFleet] = useState<FleetHealth | null>(null);

  useEffect(() => {
    const tick = () => {
      const next = compute(teamMap);
      pushHistory('up', next.up);
      pushHistory('degraded', next.degraded);
      pushHistory('down', next.down);
      pushHistory('battery', next.avgBattery ?? 0);
      pushHistory('link', next.meanLink ?? 0);
      pushHistory('sim', next.simCount);
      setSnap(next);
      force((n) => n + 1);
    };
    tick();
    const id = window.setInterval(tick, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [teamMap, aorId]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void getFleetHealth().then((r) => {
        if (!cancelled) setFleet(r.ok ? r.data : null);
      });
    };
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const subsystems = fleet?.subsystems ?? [];
  const reporting = fleet?.subsystemsReporting ?? 0;
  const subsystemsTotal = fleet?.subsystemsTotal ?? 0;

  // Predicted conflicts, on the same slow beat as the rest of this screen.
  useEffect(() => {
    const load = () =>
      void getSeparation().then((r) => setConflicts(r.ok ? r.data.conflicts : []));
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  const kpis = useMemo(
    () => [
      { key: 'up', label: 'AIRCRAFT UP', value: String(snap.up), unit: `/ ${snap.total}`, color: OK },
      { key: 'degraded', label: 'DEGRADED', value: String(snap.degraded), unit: 'links', color: WARN },
      { key: 'down', label: 'DOWN', value: String(snap.down), unit: 'no fix', color: DANGER },
      { key: 'battery', label: 'AVG BATTERY', value: snap.avgBattery === null ? '--' : String(snap.avgBattery), unit: '%', color: WHITE },
      { key: 'link', label: 'MEAN LINK', value: snap.meanLink === null ? '--' : String(snap.meanLink), unit: '% RSSI', color: WHITE },
      { key: 'sim', label: 'SIM ACTIVE', value: String(snap.simCount), unit: 'aircraft', color: WARN },
    ],
    [snap],
  );

  const maxBin = Math.max(1, ...snap.batteryBins);
  const binLabels = ['0–20', '20–40', '40–60', '60–80', '80–100'];
  const binColors = [DANGER, WARN, WARN, OK, OK];

  const alerts: DerivedAlert[] = [
    ...storeAlerts.slice(0, 4).map((a) => ({
      cs: a.droneId ?? 'FLEET',
      msg: a.message,
      tone: a.severity === 'critical' ? DANGER : WARN,
      edge:
        a.severity === 'critical'
          ? 'color-mix(in oklch, oklch(0.62 0.20 28) 45%, transparent)'
          : 'color-mix(in oklch, oklch(0.80 0.15 85) 40%, transparent)',
    })),
    ...snap.derivedAlerts,
  ].slice(0, 6);

  return (
    <div
      className="screen-canvas absolute inset-0"
      style={{ background: 'radial-gradient(1200px 700px at 72% -10%, oklch(0.19 0 0), oklch(0.12 0 0))' }}
    >
      <main
        className="scroll-thin absolute flex flex-col gap-3 overflow-y-auto"
        style={{ top: 90, bottom: 16, left: '50%', transform: 'translateX(-50%)', width: 'min(1180px, calc(100vw - 28px))' }}
      >
        {/* KPI grid */}
        <CommanderIntent />
        <CompositeHealthHero />
        <PopoutPanel title="JOINT OPERATIONS">
          <JocPanel />
        </PopoutPanel>
        <PopoutPanel title="MAINTENANCE BENCH">
          <MaintenancePanel />
        </PopoutPanel>
        <PopoutPanel title="SILVUS MESH">
          <SilvusMeshPanel />
        </PopoutPanel>
        <InventoryPanel />

        <div className="kpi-grid grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))' }}>
          {kpis.map((k) => (
            <KpiCard
              key={k.key}
              label={k.label}
              value={k.value}
              unit={k.unit}
              color={k.color}
              history={HISTORY[k.key] ?? []}
            />
          ))}
        </div>

        {/* Readiness + battery distribution */}
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))' }}>
          <Panel title="READINESS BY TEAM">
            <div className="flex flex-col gap-[13px]">
              {snap.teams.length === 0 ? (
                <p className="text-[11px]" style={{ color: MUTED }}>
                  No aircraft connected.
                </p>
              ) : (
                snap.teams.map((t) => {
                  const tot = t.up + t.degraded + t.down || 1;
                  const ready = Math.round((t.up / tot) * 100);
                  const readyColor = ready >= 80 ? OK : ready >= 60 ? WARN : DANGER;
                  return (
                    <div key={t.name}>
                      <div className="mb-[5px] flex items-center gap-2">
                        <span className="size-2 rounded-[3px]" style={{ background: t.color }} />
                        <span className="label-mono text-[11.5px]">{t.name}</span>
                        <span className="label-mono text-[10px]" style={{ color: MUTED }}>
                          {tot} AC
                        </span>
                        <span className="ml-auto label-mono text-[11px]" style={{ color: readyColor }}>
                          {ready}% READY
                        </span>
                      </div>
                      <div className="flex h-[9px] overflow-hidden rounded-[5px]" style={{ background: 'rgba(0,0,0,0.35)' }}>
                        <span style={{ width: `${(t.up / tot) * 100}%`, background: OK }} />
                        <span style={{ width: `${(t.degraded / tot) * 100}%`, background: WARN }} />
                        <span style={{ width: `${(t.down / tot) * 100}%`, background: DANGER }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-[15px] flex gap-4 label-mono text-[9.5px]" style={{ color: MUTED }}>
              {[
                ['NOMINAL', OK],
                ['DEGRADED', WARN],
                ['DOWN', DANGER],
              ].map(([l, c]) => (
                <span key={l} className="inline-flex items-center gap-[5px]">
                  <span className="size-[9px] rounded-[2px]" style={{ background: c }} />
                  {l}
                </span>
              ))}
            </div>
          </Panel>

          <Panel title="BATTERY DISTRIBUTION">
            <div className="flex h-[120px] items-end gap-1.5">
              {snap.batteryBins.map((n, i) => (
                <div key={binLabels[i]} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                  <span className="label-mono text-[10px]" style={{ color: 'rgba(244,244,245,0.6)' }}>
                    {n}
                  </span>
                  <div
                    className="w-full rounded-t-[5px]"
                    style={{ height: `${(n / maxBin) * 100}%`, minHeight: 3, background: binColors[i] }}
                  />
                  <span className="label-mono text-[8.5px]" style={{ color: 'rgba(244,244,245,0.45)' }}>
                    {binLabels[i]}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* Alerts + subsystems */}
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))' }}>
          <Panel
            title="ACTIVE ALERTS"
            right={
              <span
                className="rounded-[99px] px-2 py-0.5 label-mono text-[8px]"
                style={{ background: 'color-mix(in oklch, oklch(0.80 0.15 85) 25%, transparent)', color: WARN }}
              >
                {alerts.length}
              </span>
            }
          >
            <div className="flex flex-col gap-[7px]">
              {alerts.length === 0 ? (
                <p className="text-[11px]" style={{ color: MUTED }}>
                  No active alerts. The fleet is nominal.
                </p>
              ) : (
                alerts.map((a, i) => (
                  <div
                    key={`${a.cs}-${i}`}
                    className="flex items-center gap-2.5 rounded-[12px] border px-[11px] py-[9px]"
                    style={{ background: 'rgba(255,255,255,0.04)', borderColor: a.edge }}
                  >
                    <span className="size-[7px] shrink-0 rounded-full" style={{ background: a.tone }} />
                    <span className="label-mono text-[11px] whitespace-nowrap">{a.cs}</span>
                    <span className="min-w-0 truncate text-[11px]" style={{ color: 'rgba(244,244,245,0.7)' }}>
                      {a.msg}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Panel>

          <Panel
            title="SEPARATION"
            right={
              conflicts.length ? (
                <span
                  className="rounded-[99px] px-2 py-0.5 label-mono text-[8px]"
                  style={{
                    background: 'color-mix(in oklch, oklch(0.72 0.19 28) 25%, transparent)',
                    color: DANGER,
                  }}
                >
                  {conflicts.length}
                </span>
              ) : undefined
            }
          >
            {conflicts.length === 0 ? (
              <p className="text-[11px]" style={{ color: MUTED }}>
                No predicted conflicts. Pairs holding their distance are not listed.
              </p>
            ) : (
              <div className="flex flex-col gap-[7px]">
                {conflicts.map((c) => (
                  <div
                    key={`${c.a}-${c.b}`}
                    className="flex items-center gap-2.5 rounded-[12px] border px-[11px] py-[9px]"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      borderColor:
                        c.severity === 'critical'
                          ? 'color-mix(in oklch, oklch(0.62 0.20 28) 45%, transparent)'
                          : 'color-mix(in oklch, oklch(0.80 0.15 85) 40%, transparent)',
                    }}
                  >
                    <span
                      className="size-[7px] shrink-0 rounded-full"
                      style={{ background: c.severity === 'critical' ? DANGER : WARN }}
                    />
                    <span className="label-mono text-[11px] whitespace-nowrap">
                      {c.a} · {c.b}
                    </span>
                    <span className="tnum ml-auto text-[11px]" style={{ color: MUTED }}>
                      {c.separationM.toFixed(0)} m now → {c.closestM.toFixed(0)} m
                      {c.timeToClosestS > 0 ? ` in ${c.timeToClosestS.toFixed(0)}s` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="SUBSYSTEM STATUS">
            {subsystems.length === 0 ? (
              <p className="text-[11px]" style={{ color: 'rgba(244,244,245,0.55)' }}>
                No aircraft is reporting sensor health. This panel is blank rather than
                full, because nothing has been checked.
              </p>
            ) : (
              <div className="flex flex-col gap-[9px]">
                {subsystems.map((s) => (
                  <div key={s.name} className="flex items-center gap-2.5 text-[12px]">
                    <span
                      className="w-[120px] shrink-0 truncate phone:w-[92px]"
                      style={{ color: 'rgba(244,244,245,0.75)' }}
                      title={s.name}
                    >
                      {s.name}
                    </span>
                    <div
                      className="h-1.5 flex-1 overflow-hidden rounded-[3px]"
                      style={{ background: 'rgba(0,0,0,0.35)' }}
                    >
                      {s.percent === null ? null : (
                        <span
                          className="block h-full"
                          style={{ width: `${s.percent}%`, background: statusColor(s.percent) }}
                        />
                      )}
                    </div>
                    <span
                      className="tnum w-[76px] shrink-0 text-right text-[10.5px]"
                      style={{
                        color:
                          s.percent === null
                            ? 'rgba(244,244,245,0.4)'
                            : statusColor(s.percent),
                      }}
                    >
                      {s.percent === null
                        ? 'not fitted'
                        : s.unhealthy > 0
                          ? `${s.unhealthy} of ${s.healthy + s.unhealthy} bad`
                          : `${s.percent}%`}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div
              className="mt-[13px] text-[10.5px]"
              style={{ color: 'rgba(244,244,245,0.4)', lineHeight: 1.5 }}
            >
              {/* The denominator, said out loud. A bar drawn from two aircraft
                  out of nine is not a fleet reading, and the difference is
                  invisible in the bar itself. */}
              From the aircraft&rsquo;s own SYS_STATUS sensor health —{' '}
              {reporting} of {subsystemsTotal} aircraft reporting.
              {reporting < subsystemsTotal
                ? ' The rest have not said, which is not the same as healthy.'
                : ''}
            </div>
          </Panel>
        </div>
      </main>
    </div>
  );
}

export default HealthView;
